import axios from 'axios';
import { Product, Cart, Order, ApiResponse, CreateProductRequest, UpdateProductRequest } from '../types';
import { clearAdminToken, getAdminToken } from '../utils/adminAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach admin token when present
api.interceptors.request.use(
  (config) => {
    const token = getAdminToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Clear admin session on auth failure
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && getAdminToken()) {
      clearAdminToken();
    }
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Product API
export const productApi = {
  getAll: async (category?: string, search?: string): Promise<Product[]> => {
    const params = new URLSearchParams();
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    
    const response = await api.get<ApiResponse<Product[]>>(`/products?${params.toString()}`);
    return response.data.data;
  },

  getById: async (id: string): Promise<Product> => {
    const response = await api.get<ApiResponse<Product>>(`/products/${id}`);
    return response.data.data;
  },

  create: async (product: CreateProductRequest): Promise<Product> => {
    const response = await api.post<ApiResponse<Product>>('/products', product);
    return response.data.data;
  },

  update: async (id: string, product: UpdateProductRequest): Promise<Product> => {
    const response = await api.put<ApiResponse<Product>>(`/products/${id}`, product);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/products/${id}`);
  },

  getCategories: async (): Promise<string[]> => {
    const response = await api.get<ApiResponse<string[]>>('/products/categories');
    return response.data.data;
  },
};

// Cart API
export const cartApi = {
  create: async (id?: string): Promise<Cart> => {
    const response = await api.post<ApiResponse<Cart>>('/carts', id ? { id } : undefined);
    return response.data.data;
  },

  getById: async (id: string): Promise<Cart> => {
    const response = await api.get<ApiResponse<Cart>>(`/carts/${id}`);
    return response.data.data;
  },

  addItem: async (cartId: string, productId: string, quantity: number = 1): Promise<Cart> => {
    const response = await api.post<ApiResponse<Cart>>(`/carts/${cartId}/items`, {
      productId,
      quantity,
    });
    return response.data.data;
  },

  removeItem: async (cartId: string, productId: string): Promise<Cart> => {
    const response = await api.delete<ApiResponse<Cart>>(`/carts/${cartId}/items/${productId}`);
    return response.data.data;
  },

  updateItemQuantity: async (cartId: string, productId: string, quantity: number): Promise<Cart> => {
    const response = await api.put<ApiResponse<Cart>>(`/carts/${cartId}/items/${productId}`, {
      quantity,
    });
    return response.data.data;
  },

  clear: async (cartId: string): Promise<Cart> => {
    const response = await api.delete<ApiResponse<Cart>>(`/carts/${cartId}/items`);
    return response.data.data;
  },

  delete: async (cartId: string): Promise<void> => {
    await api.delete(`/carts/${cartId}`);
  },
};

// Order API
export const orderApi = {
  getAll: async (status?: string): Promise<Order[]> => {
    const params = status ? `?status=${status}` : '';
    const response = await api.get<ApiResponse<Order[]>>(`/orders${params}`);
    return response.data.data;
  },

  getById: async (id: string, email?: string): Promise<Order> => {
    const params = email ? `?email=${encodeURIComponent(email)}` : '';
    const response = await api.get<ApiResponse<Order>>(`/orders/${id}${params}`);
    return response.data.data;
  },

  updateStatus: async (id: string, status: Order['status']): Promise<Order> => {
    const response = await api.put<ApiResponse<Order>>(`/orders/${id}/status`, { status });
    return response.data.data;
  },

  getByEmail: async (email: string): Promise<Order[]> => {
    const response = await api.get<ApiResponse<Order[]>>(`/orders/customer/${email}`);
    return response.data.data;
  },
};

export default api;
